import { Injectable, Inject, Logger } from '@nestjs/common';
import {
  PLATFORM_HTTP_CLIENT,
  type PlatformHttpClient,
} from '@lark-apaas/nestjs-common';

const TELEMETRY_PATH = '/api/v1/studio/innerapi/resource_events';

/**
 * 事件类型
 */
export type EventType = 'install' | 'create_instance' | 'call';

/**
 * 调用者类型
 */
export type CallerType = 'client' | 'server' | 'debug';

/**
 * 资源事件
 */
export interface ResourceEvent {
  resourceType: 'plugin';
  resourceKey: string;
  eventType: EventType;
  details?: Record<string, string>;
}

/**
 * Call 事件详情
 */
export interface CallEventDetails {
  version: string;
  success: string;
  duration: string;
  is_stream: string;
  caller_type: string;
  status_code: string;
}

/**
 * 上报响应
 */
interface ReportResponse {
  status_code: string;
  message?: string;
  data?: unknown;
}

/**
 * 插件遥测服务
 *
 * 负责上报插件生命周期事件到平台
 */
@Injectable()
export class TelemetryService {
  private readonly logger = new Logger(TelemetryService.name);

  constructor(
    @Inject(PLATFORM_HTTP_CLIENT) private readonly client: PlatformHttpClient,
  ) {}

  /**
   * 上报事件到平台
   *
   * @param events - 事件列表
   * @returns 是否上报成功
   */
  async reportEvents(events: ResourceEvent[]): Promise<boolean> {
    if (events.length === 0) {
      return true;
    }

    try {
      const response = await this.client.post(TELEMETRY_PATH, { events });

      if (!response.ok) {
        this.logger.warn(`Failed to report events: ${response.status} ${response.statusText}`);
        return false;
      }

      const result = (await response.json()) as ReportResponse;

      if (result.status_code !== '0') {
        this.logger.warn(`API error: ${result.message}`);
        return false;
      }

      this.logger.debug(`Reported ${events.length} event(s) successfully`);
      return true;
    } catch (error) {
      // 事件上报失败不应影响主流程
      this.logger.warn(`Failed to report events: ${error instanceof Error ? error.message : error}`);
      return false;
    }
  }

  /**
   * 上报插件调用事件
   *
   * @param pluginKey - 插件标识
   * @param details - 调用详情
   */
  async reportCall(pluginKey: string, details: CallEventDetails): Promise<void> {
    await this.reportEvents([
      {
        resourceType: 'plugin',
        resourceKey: pluginKey,
        eventType: 'call',
        details: { ...details },
      },
    ]);
  }
}
